import type { AssignmentMedia } from '../../model/assignments/interfaces'
import type { ApiResponse } from '@shared/api'

import { getAxiosErrorMessage } from '@shared/api'
import { useAdminRoutes } from '@shared/lib/routes'
import { useToast } from '@shared/ui/toast'

interface AssignmentTemplateUploadMediaApiOptions {
  templateId: string | number
  file: File
  /** Stores under `cover/`. Does not write `cover_path` — the client follows up with a `PATCH`. */
  cover?: boolean
  /** 0–100. */
  onProgress?: (percent: number) => void
  onStart?: () => void
  onSuccess?: (media: AssignmentMedia) => void
  onError?: (message: string) => void
  onFinish?: () => void
}

/**
 * `POST …/media` — multipart image upload. Returns `{ path, url, expires_at }`: persist `path`,
 * never `url` (render-only). The path's `{templateId}` is where the object was uploaded, not where
 * it is used (duplicates share objects) — never parse an id out of it. No global loader and no
 * `Content-Type` header (axios sets the multipart boundary).
 */
export const useAssignmentTemplateUploadMediaApi = async (
  options: AssignmentTemplateUploadMediaApiOptions,
): Promise<AssignmentMedia | null> => {
  const { $axios } = useNuxtApp()
  const toast = useToast()

  const url = useAdminRoutes('admin.assignment-templates.media.store', { template_id: options.templateId })

  options.onStart?.()

  try {
    if (!url) {
      const msg = 'Unexpected error. Invalid request URL.'
      toast.error(msg)
      options.onError?.(msg)
      return null
    }

    const formData = new FormData()
    formData.append('file', options.file)

    // Omitted is the documented false.
    if (options.cover) {
      formData.append('cover', '1')
    }

    const { data } = await $axios.post<ApiResponse>(url, formData, {
      onUploadProgress: (event) => {
        if (!event.total) return
        options.onProgress?.(Math.round((event.loaded / event.total) * 100))
      },
    })

    if (!data.success) {
      toast.error(data.message)
      options.onError?.(data.message)
      return null
    }

    const media: AssignmentMedia | null = data.data?.media ?? null

    if (!media?.path) {
      const msg = 'Unexpected error. The upload response carried no file.'
      toast.error(msg)
      options.onError?.(msg)
      return null
    }

    options.onSuccess?.(media)
    return media
  } catch (err: unknown) {
    const msg = getAxiosErrorMessage(err)
    toast.error(msg)
    options.onError?.(msg)
    return null
  } finally {
    options.onFinish?.()
  }
}
